import { InnerPageActions } from "@/components/site/InnerPageActions";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { CarCard } from "@/components/site/CarCard";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { cars } from "@/lib/cars";
import {
  BUSINESS_URL,
  breadcrumbScript,
  faqScript,
  type FaqItem,
} from "@/lib/schema";

const fleetFaqs: FaqItem[] = [
  {
    question: "How many vehicles are in the Avolus fleet?",
    answer:
      "The Avolus fleet currently includes eight vehicles: the Range Rover P530 LWB, Cadillac Escalade, BMW X7 M60i, Mercedes-Benz S580, Porsche Macan, BMW M3 Competition, BMW M440i, and BMW X1. Every vehicle is a 2021-2024 model year, privately owned by Avolus, and kept unbranded: no wraps, decals, or rental markers.",
  },
  {
    question: "What's the cheapest car you rent in Vancouver?",
    answer:
      "The 2023 BMW X1 xLine is our most affordable rental at $200 for the first 24 hours, dropping to $75/day at seven-plus days. It's a black-on-black compact luxury SUV with xDrive AWD, a strong option for daily use, longer stays, and drivers who want BMW quality on a lower budget.",
  },
  {
    question: "Do you deliver anywhere in Metro Vancouver?",
    answer:
      "Yes. Complimentary delivery and collection are included within Vancouver and to YVR. Other Metro Vancouver locations, Squamish, Whistler, Abbotsford and beyond are quoted when you book. See our delivery page for coverage and pricing details.",
  },
  {
    question: "Can I rent more than one vehicle at a time?",
    answer:
      "Yes. Wedding parties, corporate groups, and film productions regularly book multiple vehicles from us simultaneously, for example an S580 for the couple and an Escalade for the wedding party. Contact us directly for multi-vehicle bookings and we'll coordinate delivery and pricing.",
  },
];

const itemListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Avolus Cars Rental Fleet",
  itemListElement: cars.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Car",
      name: c.name,
      url: `${BUSINESS_URL}/fleet/${c.slug}`,
      offers: {
        "@type": "Offer",
        price: String(c.pricePerDay),
        priceCurrency: "CAD",
        availability: "https://schema.org/InStock",
      },
    },
  })),
};

const specValue = (car: (typeof cars)[number], label: string) =>
  car.specs.find((s) => s.label.toLowerCase() === label.toLowerCase())?.value ?? "N/A";

export const Route = createFileRoute("/fleet/")({
  component: Fleet,
  head: () => ({
    meta: [
      { title: "Fleet | Luxury & Exotic Cars for Rent in Vancouver | Avolus" },
      { name: "description", content: "Compare the Avolus fleet in Vancouver: Range Rover P530 LWB, BMW X7, Mercedes S580, Escalade, Macan, M3, M440i and X1." },
      { property: "og:title", content: "Fleet | Avolus Luxury Car Rental Vancouver" },
      { property: "og:description", content: "Eight hand-selected 2021-2024 luxury vehicles, including the Range Rover P530 LWB, delivered across Metro Vancouver." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://avoluscars.com/fleet" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/fleet" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: BUSINESS_URL },
        { name: "Fleet", url: `${BUSINESS_URL}/fleet` },
      ]),
      faqScript(fleetFaqs),
      {
        type: "application/ld+json",
        children: JSON.stringify(itemListSchema),
      },
    ],
  }),
});

function Fleet() {
  return (
    <Layout inner>
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Our Rental Fleet</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground max-w-3xl">
              Luxury &amp; Exotic Car Rental Fleet Vancouver
            </h1>
            <div className="mt-6 max-w-2xl space-y-4 text-muted-foreground">
              <p>
                 The Avolus fleet is a tightly curated group of eight luxury and performance
                vehicles, all 2021-2024 model years, privately owned and maintained by us
                rather than sourced from a rotating rental pool. Every car is kept
                <span className="text-foreground"> unbranded</span>: no wraps, no decals, no
                rental-lot markers, so it looks like your own car for the weekend, the
                wedding, or the airport pickup.
              </p>
              <p>
                Complimentary delivery and collection are included within Vancouver and to YVR;
                other Metro Vancouver locations are quoted. From the
                 $200/day BMW X1 to the $395/day Range Rover P530 LWB, Mercedes-Benz S580 and Cadillac Escalade,
                the range is deliberately narrow so every vehicle earns its place.
              </p>
            </div>
            <div className="mt-6"><InnerPageActions /></div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
          <h2 className="sr-only">Available Vehicles</h2>
          <div className="grid gap-6 lg:gap-8 md:grid-cols-2 lg:grid-cols-3">
            {cars.map((c, i) => (
              <CarCard key={c.slug} car={c} index={i} booking />
            ))}
          </div>
        </div>
      </section>

      <section className="hidden md:block pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Compare the fleet</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">
              Specs and rates side by side
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl">
              Every value below is pulled live from our fleet: daily rates shown are the
              24-hour rate and the 7-day-plus rate for each vehicle.
            </p>
            <div className="hidden md:block mt-8 overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="p-4 font-medium text-foreground">Vehicle</th>
                    <th className="p-4 font-medium text-foreground">Category</th>
                    <th className="p-4 font-medium text-foreground">Seats</th>
                    <th className="p-4 font-medium text-foreground">Engine</th>
                    <th className="p-4 font-medium text-foreground">24h rate</th>
                    <th className="p-4 font-medium text-foreground">3-6 day rate</th>
                    <th className="p-4 font-medium text-foreground">7-day rate</th>
                  </tr>
                </thead>
                <tbody>
                  {cars.map((c) => (
                    <tr key={c.slug} className="border-t border-border align-top">
                      <td className="p-4">
                        <Link
                          to="/fleet/$slug"
                          params={{ slug: c.slug }}
                          className="text-foreground hover:underline"
                        >
                          {c.name}
                        </Link>
                      </td>
                      <td className="p-4 text-muted-foreground">{c.category}</td>
                      <td className="p-4 text-muted-foreground">{specValue(c, "Seats")}</td>
                      <td className="p-4 text-muted-foreground">{specValue(c, "Engine")}</td>
                      <td className="p-4 text-foreground">{c.rates.h24}</td>
                      <td className="p-4 text-foreground">{c.rates.d36}</td>
                      <td className="p-4 text-foreground">{c.rates.d7}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How to choose</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">
              Match the car to the trip
            </h2>
            <div className="mt-10 grid gap-10 md:grid-cols-2">
              <div>
                <h3 className="font-display text-xl text-foreground">Best for airport pickups and luggage</h3>
                <p className="mt-3 text-muted-foreground">
                  For YVR runs with a full complement of checked bags, the{" "}
                  <Link to="/fleet/$slug" params={{ slug: "cadillac-escalade-2023" }} className="underline hover:no-underline">Cadillac Escalade rental</Link>{" "}
                  and{" "}
                  <Link to="/fleet/$slug" params={{ slug: "bmw-x7-m60i-2024" }} className="underline hover:no-underline">BMW X7 M60i</Link>{" "}
                  handle seven and six passengers respectively with cargo room to spare. Both
                  come with complimentary curbside delivery to arrivals; see our{" "}
                  <Link to="/yvr-airport-car-rental" className="underline hover:no-underline">YVR airport rental page</Link>{" "}
                  for meet-up logistics.
                </p>
              </div>
              <div>
                <h3 className="font-display text-xl text-foreground">Best for weddings and events</h3>
                <p className="mt-3 text-muted-foreground">
                  The{" "}
                  <Link to="/fleet/$slug" params={{ slug: "mercedes-s-class-2022" }} className="underline hover:no-underline">Mercedes-Benz S580</Link>{" "}
                  is the classic wedding car: hand-stitched Nappa, executive rear seats,
                  quiet enough for photos and speeches. Pair it with the{" "}
                  <Link to="/fleet/$slug" params={{ slug: "cadillac-escalade-2023" }} className="underline hover:no-underline">Escalade</Link>{" "}
                  for the wedding party. Full logistics on our{" "}
                  <Link to="/wedding-car-rental-vancouver" className="underline hover:no-underline">wedding car rental page</Link>.
                </p>
              </div>
              <div>
                <h3 className="font-display text-xl text-foreground">Best for a Sea-to-Sky or Whistler drive</h3>
                <p className="mt-3 text-muted-foreground">
                  For the drive up Highway 99, the{" "}
                  <Link to="/fleet/$slug" params={{ slug: "bmw-m440-2021" }} className="underline hover:no-underline">BMW M440i coupe</Link>{" "}
                  and{" "}
                  <Link to="/fleet/$slug" params={{ slug: "porsche-macan-2023" }} className="underline hover:no-underline">Porsche Macan</Link>{" "}
                  are the enthusiast picks: both AWD, both happy in the corners between
                  Horseshoe Bay and Squamish. For four adults plus ski gear, step up to the{" "}
                  <Link to="/fleet/$slug" params={{ slug: "bmw-x7-m60i-2024" }} className="underline hover:no-underline">X7 M60i</Link>. Only the Escalade currently has winter tires; confirm suitability before a winter trip.
                </p>
              </div>
              <div>
                <h3 className="font-display text-xl text-foreground">Best for daily driving and city use</h3>
                <p className="mt-3 text-muted-foreground">
                  For a week in the city, Kitsilano parking spots and Yaletown garages, the{" "}
                  <Link to="/fleet/$slug" params={{ slug: "bmw-x1-2023" }} className="underline hover:no-underline">BMW X1 xLine</Link>{" "}
                  is the smart choice at $75/day for seven-plus days. Enthusiasts wanting
                  something sharper for the same budget should look at the{" "}
                  <Link to="/fleet/$slug" params={{ slug: "bmw-m3-2023" }} className="underline hover:no-underline">BMW M3 Competition</Link>.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>What's included with every rental</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">
              The same standard, whichever car you pick
            </h2>
            <ul className="mt-8 grid gap-4 md:grid-cols-2 text-muted-foreground">
              <li className="border border-border p-5">
                <span className="text-foreground">$5,000,000 third-party liability insurance</span> included on every rental.
              </li>
              <li className="border border-border p-5">
                <span className="text-foreground">150 km per day</span> included; extra kilometres billed at the per-car rate.
              </li>
              <li className="border border-border p-5">
                <span className="text-foreground">LDW at $60 per day</span> applies to every vehicle.
              </li>
              <li className="border border-border p-5">
                <span className="text-foreground">$2,000 refundable security deposit</span> on every vehicle, held on a credit card.
              </li>
              <li className="border border-border p-5">
                <span className="text-foreground">Complimentary delivery and collection</span> within Vancouver and to YVR; elsewhere quoted.
              </li>
              <li className="border border-border p-5">
                <span className="text-foreground">Unbranded vehicles</span>: no wraps, decals, or rental markers of any kind.
              </li>
              <li className="border border-border p-5 md:col-span-2">
                <span className="text-foreground">Driver requirements:</span> 25 years or older with a full, valid driver's licence. See our{" "}
                <Link to="/faq" className="underline hover:no-underline">FAQ</Link> for the full policy.
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Frequently asked questions</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">About the fleet</h2>
            <div className="mt-8 divide-y divide-border border-y border-border">
              {fleetFaqs.map((f) => (
                <details key={f.question} className="group py-5">
                  <summary className="cursor-pointer list-none text-foreground flex justify-between items-center">
                    <span>{f.question}</span>
                    <span className="ml-4 text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-muted-foreground">{f.answer}</p>
                </details>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <p className="pb-16 -mt-8 text-center text-xs text-muted-foreground">
        Own a luxury vehicle?{" "}
        <Link to="/rent-out-your-car" className="underline underline-offset-4 hover:text-foreground transition-colors">
          Rent it out with Avolus
        </Link>
      </p>
    </Layout>
  );
}
